// @ts-nocheck
import translate from "@insite/client-framework/Translate";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Button, { ButtonPresentationProps } from "@store/MobiusOverrides/Button/Button";
import Modal, { ModalPresentationProps } from "@store/MobiusOverrides/Modal/Modal";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { FC, useEffect, useRef, useState } from "react";
import { css } from "styled-components";

export interface NavigationBlockerPresentationProps extends ExpandStyles<NavigationBlockerStyles> {}

export interface NavigationBlockerProps extends NavigationBlockerPresentationProps {
    /**
     * Arms the blocker. While `true`, in-app link clicks and browser back/forward are intercepted
     * (a confirmation modal is shown), and tab-close/refresh triggers the native `beforeunload` prompt.
     * While `false`, navigation flows normally and any open modal is closed. Typically wired to a form's
     * dirty state. Defaults to `false`.
     */
    isActive?: boolean;
    /**
     * Destination paths that should never prompt — used to let intentional in-flow navigation through.
     * A navigation is allowed when its destination pathname *starts with* any entry here (matched by
     * pathname only, so query strings such as `?orderNumber=` are ignored). `undefined`/empty entries
     * are skipped, so passing an unresolved page link is safe. Does not affect the `beforeunload`
     * (tab-close/refresh) prompt, which always fires while `isActive`. Defaults to `[]` (block everything).
     */
    allowedPaths?: Array<string | undefined>;
    /** Modal heading. Defaults to the translated "Discard changes?". */
    title?: React.ReactNode;
    /** Modal body copy. Defaults to the translated "Your progress won't be saved if you leave now.". */
    text?: React.ReactNode;
    /** Label of the dismiss/stay button (keeps the user on the page). Defaults to the translated "Keep Editing". */
    stayLabel?: React.ReactNode;
    /** Label of the confirm/leave button. Defaults to the translated "Leave Anyway". */
    leaveLabel?: React.ReactNode;
    /** Invoked when the user confirms leaving (clicks the leave button), just before navigation occurs. */
    onLeave?: () => void;
}

export interface NavigationBlockerStyles {
    buttonsWrapper?: InjectableCss;
    contentWrapper?: InjectableCss;
    leaveButton?: ButtonPresentationProps;
    modal?: ModalPresentationProps;
    stayButton?: ButtonPresentationProps;
    text?: TypographyPresentationProps;
    title?: TypographyPresentationProps;
}

export const navigationBlockerStyles: NavigationBlockerStyles = {
    modal: {
        hideTitle: true,
        size: 415,
    },
    contentWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: ${pxToRem(24)};
        `,
    },
    title: {
        variant: "p",
        css: css`
            font-size: ${pxToRem(20)};
            font-weight: ${x => x.theme.design.typography.weight.bold};
            line-height: ${x => x.theme.design.typography.lineHeight.small};
            text-align: center;
            margin-bottom: 0;
        `,
    },
    text: {
        variant: "p",
        css: css`
            font-size: ${pxToRem(16)};
            text-align: center;
            margin-bottom: 0;
        `,
    },
    buttonsWrapper: {
        css: css`
            display: flex;
            gap: ${pxToRem(16)};
        `,
    },
    stayButton: {
        variant: "primary",
    },
    leaveButton: {
        variant: "tertiary",
    },
};

const NavigationBlocker: FC<NavigationBlockerProps> = ({
    extendedStyles,
    isActive = false,
    overridedStyles,
    allowedPaths = [],
    title,
    text,
    stayLabel,
    leaveLabel,
    onLeave,
}) => {
    const styles = useExpandProps(navigationBlockerStyles, extendedStyles, overridedStyles);
    const history = useHistory();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const pendingUrlRef = useRef("");
    const isConfirmingRef = useRef(false);
    const safeUrlRef = useRef("");

    /* Kept in a ref so the event listeners (bound on isActive changes) always read the latest allowlist. */
    const allowedPathsRef = useRef(allowedPaths);
    allowedPathsRef.current = allowedPaths;

    const isAllowedPath = (pathname: string) =>
        allowedPathsRef.current.some(allowed => {
            if (!allowed) {
                return false;
            }
            /* Normalize the allowlist entry to its pathname so query strings/hashes (or absolute URLs) match. */
            let allowedPathname = allowed;
            try {
                allowedPathname = new URL(allowed, window.location.origin).pathname;
            } catch {
                /* Fall back to the raw entry if it can't be parsed as a URL. */
            }
            return pathname.startsWith(allowedPathname);
        });

    /* Close the modal if dirty state is cleared externally (e.g. after submit). Also refreshes the
     * safe URL — the destination popstate should restore to — whenever the blocker arms/disarms. */
    useEffect(() => {
        if (!isActive) {
            setIsModalOpen(false);
            pendingUrlRef.current = "";
        }
        safeUrlRef.current = window.location.href;
    }, [isActive]);

    /* Block in-app link clicks; block tab-close/refresh via beforeunload. Browser back/forward is
     * handled separately below by overriding `window.onpopstate`. */
    useEffect(() => {
        const onCaptureClick = (event: MouseEvent) => {
            if (!isActive || isConfirmingRef.current) {
                return;
            }

            /* Ignore clicks that don't produce an in-page navigation: non-primary buttons, modified
             * clicks that open a new tab/window, and events another listener has already cancelled. */
            if (
                event.defaultPrevented ||
                event.button !== 0 ||
                event.metaKey ||
                event.ctrlKey ||
                event.shiftKey ||
                event.altKey
            ) {
                return;
            }

            const target = event.target as HTMLElement;
            const link = (target.tagName === "A" ? target : target.closest("a[href]")) as HTMLAnchorElement | null;

            if (!link?.href) {
                return;
            }

            /* Allow external links and links that open in a new tab/window to pass through. */
            if (link.origin !== window.location.origin || link.target === "_blank") {
                return;
            }

            /* Allow same-page navigation (hash changes on the same path). */
            const isSamePage = link.pathname === window.location.pathname && link.search === window.location.search;
            if (isSamePage) {
                return;
            }

            /* Allow explicitly whitelisted destinations (e.g. allowed in-flow navigation). */
            if (isAllowedPath(link.pathname)) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();

            pendingUrlRef.current = link.pathname + link.search + link.hash;
            setIsModalOpen(true);
        };

        const onBeforeUnload = (event: BeforeUnloadEvent) => {
            if (isActive) {
                event.preventDefault();
                /* returnValue is required for legacy browser support. */
                event.returnValue = "";
            }
        };

        window.addEventListener("click", onCaptureClick, true);
        window.addEventListener("beforeunload", onBeforeUnload);

        return () => {
            window.removeEventListener("click", onCaptureClick, true);
            window.removeEventListener("beforeunload", onBeforeUnload);
        };
    }, [isActive]);

    /*
     * Take over `window.onpopstate` while the blocker is active. Adding a capture-phase popstate
     * listener isn't sufficient because SpireRouter registers its handler via the IDL slot during
     * component mount — at popstate dispatch time all window listeners run in registration order,
     * so SpireRouter's `loadPage` fires before ours regardless of `capture: true`. Owning the IDL
     * slot ensures we're the only handler that runs on a blocked back/forward.
     */
    useEffect(() => {
        if (!isActive) {
            return;
        }

        const originalOnPopState = window.onpopstate;

        window.onpopstate = (event: PopStateEvent) => {
            if (isConfirmingRef.current) {
                /* Confirmed leave: let SpireRouter route normally. */
                originalOnPopState?.call(window, event);
                return;
            }

            /*
             * By the time popstate fires, the browser has already updated window.location to the
             * destination URL. We capture that destination, then immediately push the safe URL
             * back into the history stack to undo the navigation visually.
             */
            const destination = window.location.href;
            const destinationPath = (() => {
                try {
                    return new URL(destination).pathname;
                } catch {
                    return "";
                }
            })();

            /* Allow explicitly whitelisted destinations to proceed without prompting. */
            if (destinationPath && isAllowedPath(destinationPath)) {
                originalOnPopState?.call(window, event);
                /* Track the resulting URL so a later back-button restores to it, not to a stale value. */
                safeUrlRef.current = window.location.href;
                return;
            }

            window.history.pushState(null, "", safeUrlRef.current);

            pendingUrlRef.current = (() => {
                try {
                    const url = new URL(destination);
                    return url.pathname + url.search + url.hash;
                } catch {
                    return destination;
                }
            })();
            setIsModalOpen(true);
        };

        return () => {
            window.onpopstate = originalOnPopState;
        };
    }, [isActive]);

    const handleStay = () => {
        pendingUrlRef.current = "";
        setIsModalOpen(false);
    };

    const handleLeave = () => {
        const url = pendingUrlRef.current;
        pendingUrlRef.current = "";
        isConfirmingRef.current = true;
        setIsModalOpen(false);

        try {
            onLeave?.();
        } catch {
            /* Swallow — consumer callback must not block navigation. */
        }

        /* Isolate history.push in a microtask so its errors don't leak into the click handler, and
         * always reset the confirming ref via finally so a throw can't leave the blocker stuck. */
        Promise.resolve().then(() => {
            try {
                history.push(url);
                safeUrlRef.current = url;
            } finally {
                isConfirmingRef.current = false;
            }
        });
    };

    return (
        <Modal
            {...styles.modal}
            handleClose={handleStay}
            isOpen={isModalOpen}
            data-test-selector="navigationBlockerModal"
        >
            <StyledWrapper {...styles.contentWrapper}>
                <Typography {...styles.title}>{title ?? translate("Discard changes?")}</Typography>
                <Typography {...styles.text}>
                    {text ?? translate("Your progress won't be saved if you leave now.")}
                </Typography>
                <StyledWrapper {...styles.buttonsWrapper}>
                    <Button {...styles.stayButton} onClick={handleStay} data-test-selector="navigationBlockerStayBtn">
                        {stayLabel ?? translate("Keep Editing")}
                    </Button>
                    <Button
                        {...styles.leaveButton}
                        onClick={handleLeave}
                        data-test-selector="navigationBlockerLeaveBtn"
                    >
                        {leaveLabel ?? translate("Leave Anyway")}
                    </Button>
                </StyledWrapper>
            </StyledWrapper>
        </Modal>
    );
};

export default NavigationBlocker;
